/**
 * initialState.ts — Single source of truth for the default AppState.
 *
 * Replaces duplicate INITIAL_STATE definitions in:
 * - src/hooks/useAppStore.ts
 * - src/hooks/accountActions.ts
 *
 * @see docs/PROJECT_STATUS_AND_ROADMAP.md
 */

import { AppState } from '../app/types';
import { type ModuleId, MODULES } from '../content';
import { DEFAULT_LEARNER_NAME } from './constants';

/**
 * Generate a fresh progress object from the MODULES registry.
 * This ensures new modules are automatically included without
 * needing to update hardcoded lists in multiple files.
 */
function buildDefaultProgress(): AppState['progress'] {
  return Object.fromEntries(MODULES.map(m => [m.id, {
    cheatSheetViewed: false
  }])) as AppState['progress'];
}

/**
 * Default initial state for a new learner.
 * Used when creating accounts, resetting progress, and as fallback.
 */
export const INITIAL_STATE: AppState = {
  stats: {
    name: DEFAULT_LEARNER_NAME,
    xp: 0,
    level: 1,
    unlockedAchievements: [],
    unlockedCosmetics: [],
    labSessionsCompleted: 0
  },
  progress: buildDefaultProgress(),
  questionStates: {},
  pendingLevelUp: undefined,
  familyMastery: {},
  // FG3 (S6) — empty by default; `seedInferredMastery` populates it once
  // the learner has been placed (auto or via placement test).
  inferredMastery: {}
};

/**
 * Create a fresh reset state preserving only the learner's name.
 */
export function createResetState(learnerName: string): AppState {
  return {
    ...INITIAL_STATE,
    progress: buildDefaultProgress(),
    stats: {
      name: learnerName,
      xp: 0,
      level: 1
    }
  };
}